import type { FormType } from '@/hooks/types/aiFieldGeneration'
import type { GenerateMetadataParams, GenerateMetadataResponse } from '@/services/api/api.types'

// Configuration for AI-enhanced fields across different form types
export type AIFieldConfig = {
  formField: string
  autoGenerate?: boolean
} & (
  | {
      // Standard metadata generation
      type: 'metadata'
      apiParamField: GenerateMetadataParams['field']
      apiResponseField: keyof GenerateMetadataResponse['generated'][0]
    }
  | {
      // Specialized endpoints
      type: 'specialized'
      endpoint:
        | 'generateDemographic'
        | 'generateObjectives'
        | 'generateOutline'
        | 'generateFAQs'
        | 'generateVideoMetadata'
        | 'generateImageMetadata'
      responseField: 'demographic' | 'objectives' | 'outline' | 'questions' | 'title' | 'description'
    }
  | {
      // New unified generation endpoint
      type: 'generate'
      systemPromptTemplateId: string
      additionalParams?: Record<string, any>
    }
)

// Content Module field configurations
export const CM_FIELD_CONFIGS: Record<string, AIFieldConfig> = {
  name: {
    formField: 'name',
    type: 'generate',
    systemPromptTemplateId: 'generate_cm_title',
  },
  shortDescription: {
    formField: 'shortDescription',
    type: 'generate',
    systemPromptTemplateId: 'generate_cm_description',
  },
  introduction: {
    formField: 'introduction',
    type: 'generate',
    systemPromptTemplateId: 'generate_cm_introduction',
  },
  demographic: {
    formField: 'demographic',
    type: 'generate',
    systemPromptTemplateId: 'generate_cm_target_audience',
  },
  // sectionObjectives: {
  //   formField: 'sectionObjectives',
  //   type: 'generate',
  //   systemPromptTemplateId: 'generate_cm_objectives',
  // },
  outline: {
    formField: 'outline',
    type: 'generate',
    systemPromptTemplateId: 'generate_cm_outline',
  },
  faqs: {
    formField: 'faqs',
    type: 'specialized',
    endpoint: 'generateFAQs',
    responseField: 'questions',
    autoGenerate: true,
  },
  faqSingle: {
    formField: 'faqSingle',
    type: 'generate',
    systemPromptTemplateId: 'generate_cm_faq',
    additionalParams: { faq_list: 'faqs' },
  },
}

// Step field configurations
export const STEP_FIELD_CONFIGS: Record<string, AIFieldConfig> = {
  name: {
    formField: 'name',
    type: 'generate',
    systemPromptTemplateId: 'generate_cm_file_title',
  },
  shortDescription: {
    formField: 'shortDescription',
    type: 'generate',
    systemPromptTemplateId: 'generate_cm_file_description',
  },
  faqs: {
    formField: 'faqs',
    type: 'specialized',
    endpoint: 'generateFAQs',
    responseField: 'questions',
    autoGenerate: true,
  },
  faqSingle: {
    formField: 'faqSingle',
    type: 'generate',
    systemPromptTemplateId: 'generate_cm_faq',
    additionalParams: { faq_list: 'faqs' },
  },
}

// Helper function to get field configs by type
export function getFieldConfigs(type: FormType): Record<string, AIFieldConfig> {
  switch (type) {
    case 'cm':
      return CM_FIELD_CONFIGS
    case 'step':
      return STEP_FIELD_CONFIGS
    default:
      return {}
  }
}

// Helper function to generate a specific field using config
export function getGenerateFieldOptions(
  type: FormType,
  fieldName: string,
): (AIFieldConfig & { formType: FormType }) | null {
  const configs = getFieldConfigs(type)
  const config = configs[fieldName]

  if (!config) {
    console.warn(`No AI field config found for ${type}.${fieldName}`)
    return null
  }

  return {
    formType: type,
    ...config,
  }
}

// Helper function to get all auto-generation configs for a form type
export function getAutoGenerationConfigs(type: FormType): Array<AIFieldConfig & { formType: FormType }> {
  const configs = getFieldConfigs(type)
  return Object.values(configs)
    .filter(config => config.autoGenerate !== false)
    .map(config => ({ formType: type, ...config }))
}
